import { useState } from "react";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// ── Silent device/browser detection ──────────────────────────────
// Reads only what the browser already shares publicly — no extra
// permissions, no popups asking the user anything.
function getDeviceInfo() {
  const ua = navigator.userAgent || "";

  // Browser
  let browser = "Unknown browser";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/") && !ua.includes("OPR/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/") && !ua.includes("Chrome/")) browser = "Safari";
  else if (ua.includes("OPR/")) browser = "Opera";

  // OS
  let os = "Unknown OS";
  if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";

  // Device type + screen width (helps spot "only breaks on mobile" issues)
  const width = window.innerWidth || 0;
  const deviceType = width < 768 ? "Mobile" : "Desktop";

  return `${browser} on ${os} · ${deviceType} (${width}px wide)`;
}

// ── FeedbackWidget ─────────────────────────────────────────────
// Fixed floating 💬 button — bottom right corner, just above the tab bar.
// Always visible regardless of scroll. Tapping opens a bottom-sheet popup.
// Step 1: User picks category — 🔧 About the App / 🏢 About the Society
// Step 2: Star rating + optional comment + Submit
// Routing: App feedback → Adyantam only. Society feedback → MC + Adyantam.
//
// Along with what the person types, this also silently attaches (no extra
// typing needed from them): their phone number, the current app version,
// and basic device/browser info — all to help troubleshoot issues later.
//
// Props:
//   screenName  — display name of the current screen/tab (e.g. "Finance")
//   user        — current user object from AuthContext
//   appVersion  — current app version string (e.g. "v1.0.0"), passed down
//                 from Dashboard.jsx, which reads it from Firestore
export default function FeedbackWidget({ screenName, user, appVersion }) {
  const [showPopup,   setShowPopup]   = useState(false);
  const [category,    setCategory]    = useState("");   // "app" | "society"
  const [rating,      setRating]      = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment,     setComment]     = useState("");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [done,        setDone]        = useState(false); // submitted this session

  function openPopup() {
    setCategory("");
    setRating(0);
    setComment("");
    setError("");
    setShowPopup(true);
  }

  async function handleSubmit() {
    if (rating === 0) { setError("Please select a star rating."); return; }
    setSaving(true);
    setError("");
    try {
      await addDoc(collection(db, "feedback"), {
        uid:         user?.id || user?.uid || "",
        userName:    user?.name || "Unknown",
        role:        user?.role || "",
        flatId:      user?.flatId || "",
        mobile:      user?.mobile || "",
        appVersion:  appVersion || "",
        deviceInfo:  getDeviceInfo(),
        screen:      screenName,
        category,                        // "app" or "society"
        rating,
        comment:     comment.trim(),
        submittedAt: serverTimestamp(),
      });
      setShowPopup(false);
      setDone(true);
      // Return button to normal after 3 seconds
      setTimeout(() => setDone(false), 3000);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* ── Fixed floating button ── */}
      <button
        onClick={openPopup}
        title="Share your feedback"
        style={{
          ...S.fab,
          background: done ? "#1a7a3a" : "#25D366",  // darker green when done
        }}
      >
        {done ? "✅" : "💬"}
      </button>

      {/* ── Bottom-sheet popup ── */}
      {showPopup && (
        <div style={S.overlay} onClick={() => setShowPopup(false)}>
          <div style={S.sheet} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={S.sheetTitle}>Feedback — {screenName}</div>

            {/* ── Step 1: Category selection ── */}
            {!category && (
              <>
                <div style={S.sheetSub}>What is your feedback about?</div>
                <div style={S.categoryRow}>
                  <button style={S.categoryBtn} onClick={() => setCategory("app")}>
                    <span style={S.categoryIcon}>🔧</span>
                    <span style={S.categoryLabel}>About the App</span>
                  </button>
                  <button style={S.categoryBtn} onClick={() => setCategory("society")}>
                    <span style={S.categoryIcon}>🏢</span>
                    <span style={S.categoryLabel}>About the Society</span>
                  </button>
                </div>
                <button onClick={() => setShowPopup(false)} style={S.btnCancelSmall}>
                  Cancel
                </button>
              </>
            )}

            {/* ── Step 2: Rating + comment ── */}
            {category && (
              <>
                <div style={S.categoryBadge}>
                  {category === "app" ? "🔧 About the App" : "🏢 About the Society"}
                </div>

                {/* Star rating */}
                <div style={S.starsRow}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      style={{
                        fontSize: "30px", cursor: "pointer",
                        color: star <= (hoverRating || rating) ? "#ffa726" : "#333350",
                        transition: "color 0.15s",
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
                {rating > 0 && (
                  <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "2px" }}>
                    {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
                  </div>
                )}

                {/* Comment box */}
                <textarea
                  placeholder="Any comments? (optional — type in any language)"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  maxLength={300}
                  style={S.textarea}
                />
                <div style={{ fontSize: "10px", color: "#555570", textAlign: "right", marginTop: "2px" }}>
                  {comment.length}/300
                </div>

                {error && <div style={S.error}>{error}</div>}

                <div style={S.btnRow}>
                  <button onClick={() => setCategory("")} style={S.btnCancel}>← Back</button>
                  <button onClick={handleSubmit} disabled={saving} style={S.btnSubmit}>
                    {saving ? "Saving…" : "Submit"}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────
const S = {
  // Fixed floating action button
  fab: {
    position: "fixed",
    bottom: "72px",          // sits just above the tab bar (~60px tall)
    right: "16px",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 3px 12px rgba(0,0,0,0.4)",
    zIndex: 8000,
    transition: "background 0.3s",
  },
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 9000,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  },
  sheet: {
    background: "#1a1a24",
    borderRadius: "18px 18px 0 0",
    padding: "20px 20px 36px",
    width: "100%", maxWidth: "640px",
    boxShadow: "0 -4px 24px rgba(0,0,0,0.4)",
    border: "1px solid #2e2e40",
    borderBottom: "none",
  },
  sheetTitle: {
    fontSize: "15px", fontWeight: "700",
    color: "#e8e8f0", textAlign: "center",
    marginBottom: "6px",
  },
  sheetSub: {
    fontSize: "13px", color: "#8888a8",
    textAlign: "center", marginBottom: "20px",
  },
  // Category step
  categoryRow: {
    display: "flex", gap: "12px",
    marginBottom: "16px",
  },
  categoryBtn: {
    flex: 1,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    gap: "8px",
    padding: "18px 10px",
    background: "#0f0f13",
    border: "1px solid #2e2e40",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
  },
  categoryIcon: { fontSize: "28px" },
  categoryLabel: {
    fontSize: "12px", fontWeight: "600",
    color: "#c8c8e0", textAlign: "center",
  },
  btnCancelSmall: {
    width: "100%", padding: "10px",
    background: "transparent", border: "1px solid #2e2e40",
    borderRadius: "8px", color: "#555570",
    fontSize: "13px", cursor: "pointer",
  },
  // Rating step
  categoryBadge: {
    textAlign: "center", fontSize: "12px",
    color: "#8888a8", marginBottom: "14px",
    padding: "4px 10px",
    background: "#0f0f13",
    borderRadius: "20px",
    display: "inline-block",
    width: "fit-content",
    margin: "0 auto 14px",
  },
  starsRow: {
    display: "flex", justifyContent: "center",
    gap: "8px", marginBottom: "6px",
  },
  textarea: {
    width: "100%", minHeight: "72px",
    background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "8px", color: "#e8e8f0",
    fontSize: "13px", padding: "10px",
    resize: "vertical", marginTop: "14px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    boxSizing: "border-box",
    outline: "none",
  },
  error: {
    color: "#ff5252", fontSize: "11px",
    textAlign: "center", marginTop: "8px",
  },
  btnRow: {
    display: "flex", gap: "10px", marginTop: "14px",
  },
  btnCancel: {
    flex: 1, padding: "10px",
    background: "#2e2e40", border: "1px solid #3e3e55",
    borderRadius: "8px", color: "#8888a8",
    fontSize: "13px", fontWeight: "600", cursor: "pointer",
  },
  btnSubmit: {
    flex: 2, padding: "10px",
    background: "#6c63ff", border: "none",
    borderRadius: "8px", color: "#fff",
    fontSize: "13px", fontWeight: "700", cursor: "pointer",
  },
};
